import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import CustomSelect from "@/components/CustomSelect";
import {FileSelect} from "@/components/Form/FileSelect";
import {FormProvider, useForm} from "react-hook-form";
import {useLoadingStore} from "@/stores/loading";
import {useApplyFormStore, ApplyForm} from "@/stores/applyForm";
import {AttributeComponent, AttributeRow} from "@/components/Form/Attributes";
import * as API from "@/api";
import * as Config from "@/config";
import * as Common from "@/utils/common";
import ConfirmModal from "@/components/ConfirmModal";

type Address = {
  address1: string;
  address2: string;
  address3: string;
  kana1: string;
  kana2: string;
  kana3: string;
  prefcode: string;
  zipcode: string;
};

export default function Contact() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailAddress = searchParams.get("email_address");
  const setLoading = useLoadingStore((state) => state.setLoading);
  const applyFormStore = useApplyFormStore();
  const methods = useForm<ApplyForm>({
    defaultValues: applyFormStore.applyForm ?? {
      attributes: [],
      files: [],
    },
  });
  const attributes = useRef(applyFormStore.attributes);
  const [submitted, setSubmitted] = useState(false);
  const [isShowExpiredModal, setIsShowExpiredModal] = useState(false);

  const getAttribute = (columnName: string) => {
    return attributes.current.find(
      (item: any) => item.column_name === columnName
    );
  };

  const getAttributeById = (id: number) => {
    return attributes.current.find((item: any) => item.id === id);
  };

  const watchZipCode = methods.watch(
    `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_ZIP_CODE)?.id}`
  );
  const watchInquiryType1 = methods.watch(
    `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TYPE1)?.id}`
  );

  const showInquiryType2 = useMemo(() => {
    if (watchInquiryType1?.split(Config.SPLIT_CODE)[1] == 3) {
      methods.setValue(`attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TYPE2)?.id}`, '31399@@@10@@@その他');
      return false;
    } else {
      return true;
    }
  }, [watchInquiryType1])

  useEffect(() => {
    if (typeof watchZipCode === "string" && watchZipCode.length == 7) {
      setLoading(true);
      API.getAddressByZipCode(watchZipCode)
        .then((result) => {
          if (result.data.results.length > 0) {
            setAddress(result.data.results[0]);
          } else {
            setAddress(null);
          }
        })
        .catch((error) => {
          setAddress(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [watchZipCode]);

  // onBeforeunloadイベントのハンドラー関数
  const handleBeforeUnload = (e: any) => {
    // ページ遷移やリロードのキャンセル
    e.preventDefault();
    // カスタムメッセージの設定（ブラウザによっては無視される）
    e.returnValue =
      "入力内容が未送信です。入力内容が消えますがよろしいでしょうか？";
  };

  // コンポーネントがマウントされた時にイベントを登録
  useEffect(() => {
    if (attributes.current.length == 0) {
      setLoading(true);
      API.checkExpiryDate(emailAddress)
        .then((result) => {
          if (result.data.expired == true) {
            setLoading(false);
            setIsShowExpiredModal(true);
          } else {
            fetchData();
          }
        })
        .catch((error) => {
          fetchData();
        });
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    // コンポーネントがアンマウントされた時にイベントを解除
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const {errors} = methods.formState;
    if (submitted == true) {
      if (errors.attributes || errors.files) {
        window.scrollTo({top: 0, left: 0, behavior: "smooth"});
      }
      setSubmitted(false);
    }
  }, [methods.formState]);

  const fetchData = () => {
    API.getAttributeByColumnName(Config.ATTRIBUTE_COLUMN_NAMES)
      .then((result) => {
        const data = result.data.attributes;
        applyFormStore.setAttributes(data);
        attributes.current = data;
        initValues();
      })
      .catch((error) => {
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const initValues = () => {
    const emailAddressAttribute = getAttribute(
      Config.ATTRIBUTE_COLUMN_NAME_EMAIL_ADDRESS
    );
    methods.setValue(`attributes.${emailAddressAttribute?.id}`, emailAddress);
  };

  const setAddress = (address: Address | null) => {
    if (address == null) {
      methods.setValue(
        `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_PREF)?.id}`,
        null
      );
      methods.setValue(
        `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_CITY)?.id}`,
        null
      );
      methods.setValue(
        `attributes.${
          getAttribute(Config.ATTRIBUTE_COLUMN_NAME_HOUSE_NUMBER)?.id
        }`,
        null
      );
    } else {
      methods.setValue(
        `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_PREF)?.id}`,
        address.address1
      );
      methods.setValue(
        `attributes.${getAttribute(Config.ATTRIBUTE_COLUMN_NAME_CITY)?.id}`,
        address.address2
      );
      methods.setValue(
        `attributes.${
          getAttribute(Config.ATTRIBUTE_COLUMN_NAME_HOUSE_NUMBER)?.id
        }`,
        address.address3
      );
    }
  };

  const onSubmit = (data: any) => {
    setSubmitted(true);
    methods.handleSubmit(submit)(data);
  };

  const submit = (data: any) => {
    console.log(data);
    applyFormStore.setApplyForm(data);
    navigate("/apply_confirm");
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit}>
        <div className="uk-flex uk-flex-column uk-flex-middle">
          <div className="sub-header">
            <p className="uk-text-large uk-text-bold">DRM-PF問合せ 入力画面</p>
          </div>
          <div className="uk-flex uk-width-3-4 uk-margin-top">
            <div className="step-content">
              <ul className="step">
                <li>メールアドレスの入力</li>
                <li>メールアドレスの送信完了</li>
                <li className="is-current">お客様情報の登録</li>
                <li>登録内容の確認</li>
                <li>お申し込みの完了</li>
              </ul>
            </div>
          </div>
          {attributes.current.length > 0 && (
            <div className="uk-width-3-4 uk-margin-top uk-margin-bottom">
              {renderError()}
              <div>必要事項をご入力の上、送信ボタンを押してください</div>
              <div className="uk-margin-top">
                <div className="uk-text-medium">▼問合せ者情報</div>
                <table className="form-tbl">
                  <tbody>
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_INQUIRER_TYPE
                    )}
                    rules={{
                      required: Config.MESSAGE_NO_E08("道路管理者区分"),
                    }}
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_GROUP_TYPE
                    )}
                    rules={{required: Config.MESSAGE_NO_E08("団体区分")}}
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_GROUP_NAME
                    )}
                    rules={{
                      required: Config.MESSAGE_NO_E08("法人・団体名"),
                      maxLength: {
                        value: 30,
                        message: Config.MESSAGE_NO_E35("法人・団体名", 30),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_DEPARTMENT
                    )}
                    rules={{
                      maxLength: {
                        value: 64,
                        message: Config.MESSAGE_NO_E35("部署名・所属", 64),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_SECTION
                    )}
                    rules={{
                      maxLength: {
                        value: 64,
                        message: Config.MESSAGE_NO_E35("課名", 64),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_UNIT
                    )}
                    rules={{
                      maxLength: {
                        value: 64,
                        message: Config.MESSAGE_NO_E35("室名または係名", 64),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_POSITION
                    )}
                    rules={{
                      maxLength: {
                        value: 64,
                        message: Config.MESSAGE_NO_E35("役職", 64),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <tr>
                    <td>
                      氏 名<span className="required">必須</span>
                    </td>
                    <td>
                      <div className="uk-grid-small" data-uk-grid>
                        <div className="uk-flex">
                          <p className="width-32">姓</p>
                          <AttributeComponent
                            attribute={getAttribute(
                              Config.ATTRIBUTE_COLUMN_NAME_LAST_NAME
                            )}
                            rules={{
                              required: Config.MESSAGE_NO_E08("姓"),
                              maxLength: {
                                value: 60,
                                message: Config.MESSAGE_NO_E35("姓", 60),
                              },
                            }}
                          />
                        </div>
                        <div className="uk-flex">
                          <p className="width-32">名</p>
                          <AttributeComponent
                            attribute={getAttribute(
                              Config.ATTRIBUTE_COLUMN_NAME_FIRST_NAME
                            )}
                            rules={{
                              required: Config.MESSAGE_NO_E08("名"),
                              maxLength: {
                                value: 60,
                                message: Config.MESSAGE_NO_E35("名", 60),
                              },
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      ふりがな<span className="required">必須</span>
                    </td>
                    <td>
                      <div className="uk-grid-small" data-uk-grid>
                        <div className="uk-flex">
                          <p className="width-32">せい</p>
                          <AttributeComponent
                            attribute={getAttribute(
                              Config.ATTRIBUTE_COLUMN_NAME_LAST_NAME_KANA
                            )}
                            rules={{
                              required: Config.MESSAGE_NO_E08("せい"),
                              maxLength: {
                                value: 60,
                                message: Config.MESSAGE_NO_E35("せい", 60),
                              },
                            }}
                          />
                        </div>
                        <div className="uk-flex">
                          <p className="width-32">めい</p>
                          <AttributeComponent
                            attribute={getAttribute(
                              Config.ATTRIBUTE_COLUMN_NAME_FIRST_NAME_KANA
                            )}
                            rules={{
                              required: Config.MESSAGE_NO_E08("めい"),
                              maxLength: {
                                value: 60,
                                message: Config.MESSAGE_NO_E35("めい", 60),
                              },
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      電話<span className="required">必須</span>
                    </td>
                    <td>
                      <div className="uk-flex">
                        <AttributeComponent
                          type="tel"
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_PHONE_NUMBER
                          )}
                          rules={{
                            required: Config.MESSAGE_NO_E08("電話"),
                            maxLength: {
                              value: 13,
                              message: Config.MESSAGE_NO_E35("電話", 13),
                            },
                          }}
                        />
                        <p className="uk-margin-left">例）0312345678</p>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>住所</td>
                    <td>
                      <div className="uk-flex">
                        <p className="width-110">郵便番号</p>
                        <AttributeComponent
                          type="text"
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_ZIP_CODE
                          )}
                          rules={{
                            maxLength: {
                              value: 7,
                              message: Config.MESSAGE_NO_E35("郵便番号", 7),
                            },
                          }}
                        />
                        <p className="uk-margin-left">例）1000001</p>
                      </div>
                      <div className="uk-flex uk-margin-small-top">
                        <p className="width-110">都道府県</p>
                        <AttributeComponent
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_PREF
                          )}
                          rules={{
                            maxLength: {
                              value: 128,
                              message: Config.MESSAGE_NO_E35("都道府県", 128),
                            },
                          }}
                          disabled
                        />
                      </div>
                      <div className="uk-flex uk-margin-small-top">
                        <p className="width-110">市区町村</p>
                        <AttributeComponent
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_CITY
                          )}
                          rules={{
                            maxLength: {
                              value: 128,
                              message: Config.MESSAGE_NO_E35("市区町村", 128),
                            },
                          }}
                          className="uk-width-expand"
                          disabled
                        />
                      </div>
                      <div className="uk-flex uk-margin-small-top">
                        <p className="width-110">町名番地</p>
                        <AttributeComponent
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_HOUSE_NUMBER
                          )}
                          rules={{
                            maxLength: {
                              value: 128,
                              message: Config.MESSAGE_NO_E35("町名番地", 128),
                            },
                          }}
                          className="uk-width-expand"
                        />
                      </div>
                      <div className="uk-flex uk-margin-small-top">
                        <p className="width-110">建物名・部屋番号</p>
                        <AttributeComponent
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_BUILDING_NAME
                          )}
                          rules={{
                            maxLength: {
                              value: 128,
                              message: Config.MESSAGE_NO_E35(
                                "建物名・部屋番号",
                                128
                              ),
                            },
                          }}
                          className="uk-width-expand"
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>メールアドレス</td>
                    <td>
                      {emailAddress}
                      <AttributeComponent
                        attribute={getAttribute(
                          Config.ATTRIBUTE_COLUMN_NAME_EMAIL_ADDRESS
                        )}
                        hidden
                      />
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div className="uk-margin-top">
                <div className="uk-text-medium">▼問合せ内容</div>
                <table className="form-tbl">
                  <tbody>
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TYPE1
                    )}
                    rules={{required: Config.MESSAGE_NO_E08("問合せ区分1")}}
                  />
                  {showInquiryType2 &&
                      <AttributeRow
                          attribute={getAttribute(
                            Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TYPE2
                          )}
                          rules={{required: Config.MESSAGE_NO_E08("問合せ区分2")}}
                      />
                  }
                  <AttributeRow
                    attribute={getAttribute(
                      Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_TITLE
                    )}
                    rules={{
                      required: Config.MESSAGE_NO_E08("問合せタイトル"),
                      maxLength: {
                        value: 1000,
                        message: Config.MESSAGE_NO_E35(
                          "問合せタイトル",
                          1000
                        ),
                      },
                    }}
                    className="uk-width-1-1"
                  />
                  <tr>
                    <td>
                      問合せ内容<span className="required">必須</span>
                    </td>
                    <td>
                      <AttributeComponent
                        attribute={getAttribute(
                          Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_DETAIL
                        )}
                        rules={{
                          required: Config.MESSAGE_NO_E08("問合せ内容"),
                          maxLength: {
                            value: 1000,
                            message: Config.MESSAGE_NO_E35(
                              "問合せ内容",
                              1000
                            ),
                          },
                        }}
                        className="uk-width-1-1 height-122"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>ファイル添付</td>
                    <td>
                      <div className="uk-grid-small uk-grid">
                        <FileSelect
                          name="files"
                          rules={{
                            validate: {
                              maxSizeSum: Common.maxSizeSum,
                            },
                          }}
                          description="複数ファイル選択可能：合計容量は10MBまでとなります。"
                          multiple
                        />
                      </div>
                    </td>
                  </tr>
                  {renderPersonalInformationHandling()}
                  </tbody>
                </table>
              </div>
              <div className="uk-flex uk-flex-center uk-margin-top uk-margin-large-bottom">
                <button
                  className="uk-button--l uk-button-refer uk-margin-left"
                  type="submit"
                >
                  入力内容を確認
                </button>
              </div>
            </div>
          )}
        </div>
        <ConfirmModal
          text={"期限切れました。"}
          confirmButtonText="OK"
          isShow={isShowExpiredModal}
          onConfirm={() => {
            navigate("/apply_mail_input");
          }}
        />
      </form>
    </FormProvider>
  );

  function renderPersonalInformationHandling() {
    const {
      formState: {errors},
    } = methods;
    const attr = getAttribute(
      Config.ATTRIBUTE_COLUMN_NAME_PERSONAL_INFORMATION_HANDLING
    );
    const error = errors.attributes?.[attr.id]?.message;
    return (
      <tr>
        <td>
          個人情報の取扱い<span className="required">必須</span>
        </td>
        <td>
          <p>
            下記の内容をご確認いただき、「同意する」にチェックをして内容を送信して下さい。
          </p>
          <div className="agree-box">
            <p className="title">個人情報の取扱いについて</p>
            <p>
              （一財）日本デジタル道路地図協会（以下「当協会」といいます）は、以下のとおり個人情報の保護を推進致します。
            </p>
            <p className="subtitle">個人情報の管理</p>
            <p>
              当協会は、お客さまの個人情報を正確かつ最新の状態に保ち、個人情報への不正アクセス・紛失・破損・改ざん・漏洩などを防止するため、セキュリティシステムの維持・管理体制の整備等の必要な措置を講じ、安全対策を実施し個人情報の厳重な管理を行ないます。
            </p>
            <p className="subtitle">個人情報の利用目的</p>
            <p>
              お客さまからお預かりした個人情報は、当協会からのご連絡や業務のご案内やご質問に対する回答として、電子メールや資料のご送付に利用いたします。
            </p>
            <p className="subtitle">個人情報の第三者への開示・提供の禁止</p>
            <p>
              当協会は、お客さまよりお預かりした個人情報を適切に管理し、次のいずれかに該当する場合を除き、個人情報を第三者に開示いたしません。
            </p>
            <p>・お客さまの同意がある場合</p>
            <p>
              ・お客さまが希望されるサービスを行なうために当社が業務を委託する業者に対して開示する場合
            </p>
            <p>・法令に基づき開示することが必要である場合</p>
            <p className="subtitle">個人情報の安全対策</p>
            <p>
              当協会は、個人情報の正確性及び安全性確保のために、セキュリティに万全の対策を講じています。
            </p>
            <p>法令、規範の遵守と見直し</p>
            <p>
              当協会は、保有する個人情報に関して適用される日本の法令、その他規範を遵守するとともに、本ポリシーの内容を適宜見直し、その改善に努めます。
            </p>
            <p className="subtitle">お問い合せ</p>
            <p>
              当協会の個人情報の取扱に関するお問い合せは下記までご連絡ください。
            </p>
            <p>（一財）日本デジタル道路地図協会</p>
            <p>〒102-0093</p>
            <p>東京都千代田区平河町1丁目3番13号平河町フロントビル5階</p>
            <p>TEL:03-3222-7990 FAX:03-3222-7991</p>
          </div>
          <div className="uk-flex uk-flex-middle">
            <label>
              <AttributeComponent
                attribute={getAttribute(
                  Config.ATTRIBUTE_COLUMN_NAME_PERSONAL_INFORMATION_HANDLING
                )}
                rules={{required: Config.MESSAGE_NO_E08("個人情報の取扱い")}}
                customOption="同意する"
              />{" "}
            </label>
          </div>
        </td>
      </tr>
    );
  }

  function renderError() {
    const {errors} = methods.formState;
    let list: any[] = [];
    const fileErrors = () => {
      return [errors.files?.root?.message].filter((item) => !!item);
    };
    if (errors.attributes) {
      const attributes = errors.attributes as (any | undefined)[];
      const array = attributes
        .map((item, index) => {
          const attr = getAttributeById(index);
          return {
            ...item,
            id: index,
            index: Config.ATTRIBUTE_COLUMN_NAMES.indexOf(attr?.column_name),
          };
        })
        .filter((item) => !!item.message)
        .sort((a, b) => {
          return a.index - b.index;
        });
      let attributesErrors: any[] = [];
      let fileErrorsAdded = false;
      array.forEach((item) => {
        if (
          item.index >
          Config.ATTRIBUTE_COLUMN_NAMES.indexOf(
            Config.ATTRIBUTE_COLUMN_NAME_INQUIRY_DETAIL
          ) &&
          !fileErrorsAdded
        ) {
          attributesErrors = attributesErrors.concat(fileErrors());
          fileErrorsAdded = true;
        }
        attributesErrors.push(item.message);
      });
      list = list.concat(attributesErrors);
    } else {
      list = list.concat(fileErrors());
    }
    list = list.filter((item) => !!item);

    if (list.length > 0) {
      return (
        <>
          <div className="error-box uk-width-1-1">入力エラーがあります</div>
          <div className="uk-margin-small-top uk-margin-bottom">
            {list.map((item: any) => {
              return <div className="error uk-margin-left">・{item}</div>;
            })}
          </div>
        </>
      );
    }
  }
}
